import { useNavigate } from "react-router-dom";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

export default function EkaFoundationSection() {
  const navigate = useNavigate();
  const { ref: sectionRef, isVisible: sectionVisible } = useScrollAnimation();
  const { ref: contentRef, isVisible: contentVisible } = useScrollAnimation();

  return (
    <section
      className="py-20 px-4 md:px-8 lg:px-12 bg-white"
      aria-labelledby="eka-foundation-heading"
    >
      <div className="max-w-6xl mx-auto">
        {/* Section header */}
        <div
          ref={sectionRef}
          className={`text-center mb-14 transition-all duration-700 ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <span className="text-primary text-sm font-semibold tracking-widest uppercase">Featured Foundation Partner</span>
          <h2
            id="eka-foundation-heading"
            className="text-3xl md:text-4xl font-bold text-stone-900 mt-2 mb-4 font-playfair"
          >
            EKA DAVID MEMORIAL Foundation
          </h2>
          <p className="text-primary text-sm font-medium italic max-w-xl mx-auto">
            EKA Foundation — A step towards becoming compliant with UN Sustainable Development Goals.
          </p>
        </div>

        {/* Main content card */}
        <div
          ref={contentRef}
          className={`transition-all duration-700 delay-100 ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-3xl overflow-hidden border border-stone-100">
            {/* Left — image + logo */}
            <div className="relative h-72 lg:h-auto min-h-[360px]">
              <img
                src="https://firebasestorage.googleapis.com/v0/b/bhodevcameroon.appspot.com/o/project-assets%2Frenovation-and-reconstruction-of-bomaka-secondary-school-buildings%2FIMG-20231209-WA0067.jpg?alt=media&token=0dc5b1d0-5333-4f67-b3c8-0a9c1cd8d79c"
                alt="EKA David Memorial Foundation headquarters"
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
              {/* Logo placeholder */}
              <div className="absolute bottom-5 left-5 bg-white rounded-2xl px-4 py-3 flex items-center gap-3 shadow-lg">
                <div className="w-10 h-10 flex items-center justify-center bg-primary/10 rounded-xl shrink-0">
                  <i className="ri-award-fill text-primary text-xl"></i>
                </div>
                <div>
                  <div className="text-stone-900 text-xs font-bold leading-tight">EKA Foundation</div>
                  <div className="text-stone-500 text-xs">Company Logo</div>
                </div>
              </div>
              {/* SDG badge */}
              <div className="absolute top-5 right-5 bg-primary rounded-xl px-3 py-2 text-white text-center">
                <div className="text-sm font-bold">SDG</div>
                <div className="text-xs text-white/80">Aligned</div>
              </div>
            </div>

            {/* Right — content */}
            <div className="bg-stone-50 p-8 md:p-10 flex flex-col justify-between">
              <div>
                <h3 className="text-xl font-bold text-stone-900 mb-5 font-playfair">What is EKA Foundation?</h3>

                <div className="space-y-4 text-stone-600 text-sm leading-relaxed">
                  <p>
                    Complex problem solving requires fresh ideas, innovative thinking, and a willingness to cooperate in new ways.
                  </p>
                  <p>
                    Even though the world is improving overall, many large-scale challenges still need to be tackled. The{" "}
                    <strong className="text-stone-800">global refugee-crisis</strong> affects all layers of society — devastating the lives of those of displacement and adding massive pressure of the hosting countries and delaying economic growth and prosperity for the countries affected by conflict, which ultimately affects the world as a whole.
                  </p>
                  <p>
                    Dynamic partnerships, in the spirit of the{" "}
                    <strong className="text-stone-800">Sustainable Development Goals</strong>, between non-governmental organizations, duty bearers and private{" "}
                    <strong className="text-stone-800">foundations</strong> and companies can and will become an important part of the solution.
                  </p>
                </div>

                {/* SDG tags */}
                <div className="flex flex-wrap gap-2 mt-6">
                  {["SDG 1", "SDG 3", "SDG 4", "SDG 5", "SDG 6", "SDG 16", "SDG 17"].map((sdg) => (
                    <span
                      key={sdg}
                      className="text-primary text-xs font-bold border border-primary/30 bg-primary/5 px-2.5 py-1 rounded-full whitespace-nowrap"
                    >
                      {sdg}
                    </span>
                  ))}
                </div>

                {/* Key highlights */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
                  {[
                    { icon: "ri-shake-hands-line", label: "NGO Partnership" },
                    { icon: "ri-earth-line", label: "Global Reach" },
                    { icon: "ri-leaf-line", label: "Sustainable Impact" },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 bg-white rounded-xl p-3 border border-stone-100">
                      <div className="w-8 h-8 flex items-center justify-center bg-primary/10 rounded-lg shrink-0">
                        <i className={`${item.icon} text-primary text-sm`}></i>
                      </div>
                      <span className="text-stone-700 text-xs font-semibold">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => navigate("/eka-foundation")}
                  className="inline-flex items-center justify-center gap-2 bg-primary text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-primary-dark transition-all duration-300 shadow-lg shadow-primary/25 whitespace-nowrap cursor-pointer"
                >
                  Know More About the Foundation <i className="ri-arrow-right-line"></i>
                </button>
                <a
                  href="/contact"
                  className="inline-flex items-center justify-center gap-2 border border-stone-200 text-stone-700 px-6 py-3 rounded-full text-sm font-semibold hover:border-primary hover:text-primary transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-mail-line"></i> Get in Touch
                </a>
              </div>
            </div>
          </div>

          {/* Bottom stats strip */}
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { icon: "ri-group-line", value: "50K+", label: "People Impacted" },
              { icon: "ri-map-pin-line", value: "4", label: "Core Sectors" },
              { icon: "ri-seedling-line", value: "8+", label: "SDGs Addressed" },
              { icon: "ri-building-4-line", value: "2020", label: "Partnership Since" },
            ].map((stat, i) => (
              <div
                key={i}
                className="flex items-center gap-3 bg-stone-50 rounded-2xl p-4 border border-stone-100"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-primary/10 rounded-xl shrink-0">
                  <i className={`${stat.icon} text-primary text-lg`}></i>
                </div>
                <div>
                  <div className="text-stone-900 font-bold text-base">{stat.value}</div>
                  <div className="text-stone-500 text-xs">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
